import {
  type PropsWithChildren,
  createContext,
  useContext,
  useMemo,
} from 'react';

import type { AppearancePreference } from '../types';

export interface ChatRendererConfig {
  insert?: (message: string) => void;
  append?: (message: string) => void;
  navigate?: (path: any) => void;
  isEfitnessAuthenticated?: () => boolean;
  logo?: string;
  appearancePreference?: AppearancePreference;
}

export const ChatRendererContext = createContext<ChatRendererConfig | null>(
  null,
);

interface ChatRendererProps extends PropsWithChildren {
  insert?: (message: string) => void;
  append?: (message: string) => void;
  navigate?: (path: any) => void;
  isEfitnessAuthenticated?: () => boolean;
  logo?: string;
  appearancePreference?: AppearancePreference;
}

/**
 * Provider for chat renderer, provides options for chat renderer.
 */
export function ChatRendererProvider({
  insert,
  append,
  navigate,
  isEfitnessAuthenticated,
  logo,
  appearancePreference = 'dark',
  children,
}: ChatRendererProps) {
  const contextValue = useMemo(
    () => ({
      insert,
      append,
      navigate,
      isEfitnessAuthenticated,
      logo,
      appearancePreference,
    }),
    [
      insert,
      append,
      navigate,
      isEfitnessAuthenticated,
      logo,
      appearancePreference,
    ],
  );

  return (
    <ChatRendererContext.Provider value={contextValue}>
      {children}
    </ChatRendererContext.Provider>
  );
}

/**
 * Hook for accessing chat renderer context.
 */
export function useChatRenderer() {
  const context = useContext(ChatRendererContext);

  if (!context) {
    throw new Error('Missing ChatConfigContext.Provider in the tree');
  }

  return context;
}
